import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Check, X as XIcon, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_SECS = 120;

function encodeWav(buffer, startS, endS) {
  const sr = buffer.sampleRate;
  const s = Math.max(0, Math.floor(startS * sr));
  const e = Math.min(buffer.length, Math.floor(endS * sr));
  const len = Math.max(1, e - s);
  // downmix to mono
  const mono = new Float32Array(len);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const d = buffer.getChannelData(ch);
    for (let i = 0; i < len; i++) mono[i] += d[s + i] / buffer.numberOfChannels;
  }
  const out = new DataView(new ArrayBuffer(44 + len * 2));
  const wstr = (o, str) => { for (let i = 0; i < str.length; i++) out.setUint8(o + i, str.charCodeAt(i)); };
  wstr(0, "RIFF"); out.setUint32(4, 36 + len * 2, true); wstr(8, "WAVE");
  wstr(12, "fmt "); out.setUint32(16, 16, true); out.setUint16(20, 1, true);
  out.setUint16(22, 1, true); out.setUint32(24, sr, true);
  out.setUint32(28, sr * 2, true); out.setUint16(32, 2, true); out.setUint16(34, 16, true);
  wstr(36, "data"); out.setUint32(40, len * 2, true);
  for (let i = 0; i < len; i++) {
    const v = Math.max(-1, Math.min(1, mono[i]));
    out.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Blob([out.buffer], { type: "audio/wav" });
}

export default function VoiceRecorderModal({ onCancel, onUse, title = "Record your voice-over" }) {
  const [phase, setPhase] = useState("idle");       // idle | recording | review
  const [secs, setSecs] = useState(0);
  const [level, setLevel] = useState(0);
  const [duration, setDuration] = useState(0);
  const [trim, setTrim] = useState([0, 0]);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);

  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const ctxRef = useRef(null);
  const bufferRef = useRef(null);
  const srcRef = useRef(null);
  const canvasRef = useRef(null);

  const cleanupStream = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };
  const stopPlayback = () => {
    try { srcRef.current?.stop(); } catch {}
    srcRef.current = null;
    setPlaying(false);
  };
  useEffect(() => () => { cleanupStream(); stopPlayback(); ctxRef.current?.close?.(); }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      recRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        cleanupStream();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        try {
          const ctx = ctxRef.current || new (window.AudioContext || window.webkitAudioContext)();
          ctxRef.current = ctx;
          const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
          bufferRef.current = buf;
          setDuration(buf.duration);
          setTrim([0, buf.duration]);
          setPhase("review");
        } catch {
          toast.error("Could not process the recording — try again");
          setPhase("idle");
        }
      };
      rec.start();
      setSecs(0);
      setPhase("recording");
      timerRef.current = setInterval(() => setSecs((v) => {
        if (v + 1 >= MAX_SECS) recRef.current?.stop();
        return v + 1;
      }), 1000);
      // live input level
      const actx = ctxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = actx;
      const an = actx.createAnalyser();
      an.fftSize = 512;
      actx.createMediaStreamSource(stream).connect(an);
      const data = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteTimeDomainData(data);
        let peak = 0;
        for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i] - 128) / 128);
        setLevel(peak);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      toast.error("Microphone access denied — allow the mic permission and try again");
    }
  };

  // waveform once in review
  useEffect(() => {
    if (phase !== "review" || !canvasRef.current || !bufferRef.current) return;
    const cv = canvasRef.current;
    const w = (cv.width = cv.offsetWidth * 2);
    const h = (cv.height = 96);
    const g = cv.getContext("2d");
    g.clearRect(0, 0, w, h);
    const d = bufferRef.current.getChannelData(0);
    const step = Math.max(1, Math.floor(d.length / w));
    g.fillStyle = "#8b5cf6";
    for (let x = 0; x < w; x++) {
      let peak = 0;
      for (let i = x * step; i < (x + 1) * step && i < d.length; i++) peak = Math.max(peak, Math.abs(d[i]));
      const bh = Math.max(2, peak * (h - 8));
      g.fillRect(x, (h - bh) / 2, 1, bh);
    }
  }, [phase]);

  const previewTrim = () => {
    if (playing) { stopPlayback(); return; }
    const ctx = ctxRef.current;
    const src = ctx.createBufferSource();
    src.buffer = bufferRef.current;
    src.connect(ctx.destination);
    src.onended = () => setPlaying(false);
    src.start(0, trim[0], Math.max(0.05, trim[1] - trim[0]));
    srcRef.current = src;
    setPlaying(true);
  };

  const useRecording = async () => {
    stopPlayback();
    setBusy(true);
    try {
      const blob = encodeWav(bufferRef.current, trim[0], trim[1]);
      const stamp = new Date().toTimeString().slice(0, 5).replace(":", "");
      onUse(new File([blob], `vo-recording-${stamp}.wav`, { type: "audio/wav" }));
    } catch {
      toast.error("Export failed — try again");
      setBusy(false);
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const trimPct = (v) => (duration ? (v / duration) * 100 : 0);

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" data-testid="vo-recorder-modal">
      <div className="w-full max-w-lg rounded-2xl border border-line bg-bg-elevated p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber">// {title}</div>
          <button type="button" onClick={() => { cleanupStream(); stopPlayback(); onCancel(); }}
            className="text-ink-muted hover:text-ink-primary" data-testid="vo-cancel-btn" aria-label="Close recorder">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {phase === "idle" && (
          <div className="flex flex-col items-center py-8">
            <button type="button" onClick={startRecording} data-testid="vo-record-btn"
              className="h-16 w-16 rounded-full bg-rose-500 hover:bg-rose-400 grid place-items-center text-white transition">
              <Mic className="h-7 w-7" />
            </button>
            <p className="mt-4 text-xs text-ink-secondary">Tap to start recording (up to {MAX_SECS / 60} min)</p>
            <p className="mt-1 text-[10px] text-ink-muted">Your browser will ask for microphone permission.</p>
          </div>
        )}

        {phase === "recording" && (
          <div className="flex flex-col items-center py-6">
            <div className="flex items-center gap-2 text-rose-400">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-500 animate-pulse" />
              <span className="font-mono text-2xl text-ink-primary" data-testid="vo-record-timer">{fmt(secs)}</span>
            </div>
            <div className="mt-4 h-2 w-56 rounded-full bg-black/40 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-400 transition-[width] duration-75"
                   style={{ width: `${Math.min(100, level * 130)}%` }} />
            </div>
            <button type="button" onClick={() => recRef.current?.stop()} data-testid="vo-stop-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-rose-500 hover:bg-rose-400 px-5 py-2.5 text-xs font-semibold text-white transition">
              <Square className="h-3.5 w-3.5" /> Stop recording
            </button>
          </div>
        )}

        {phase === "review" && (
          <div>
            <div className="relative rounded-lg overflow-hidden bg-black/40 border border-line">
              <canvas ref={canvasRef} className="w-full h-24 block" />
              <div className="absolute inset-y-0 left-0 bg-black/70" style={{ width: `${trimPct(trim[0])}%` }} />
              <div className="absolute inset-y-0 right-0 bg-black/70" style={{ width: `${100 - trimPct(trim[1])}%` }} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">
                  <span>Trim start</span><span>{trim[0].toFixed(1)}s</span>
                </div>
                <input type="range" min={0} max={duration} step={0.1} value={trim[0]} data-testid="vo-trim-start"
                  onChange={(e) => { stopPlayback(); setTrim(([, b]) => [Math.min(Number(e.target.value), b - 0.1), b]); }}
                  className="w-full accent-[#8b5cf6]" />
              </div>
              <div>
                <div className="flex justify-between font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">
                  <span>Trim end</span><span>{trim[1].toFixed(1)}s</span>
                </div>
                <input type="range" min={0} max={duration} step={0.1} value={trim[1]} data-testid="vo-trim-end"
                  onChange={(e) => { stopPlayback(); setTrim(([a]) => [a, Math.max(Number(e.target.value), a + 0.1)]); }}
                  className="w-full accent-[#8b5cf6]" />
              </div>
            </div>
            <div className="mt-1 text-[10px] text-ink-muted">
              Take: {fmt(duration)} · Using: {(trim[1] - trim[0]).toFixed(1)}s
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              <div className="flex gap-2">
                <button type="button" onClick={previewTrim} data-testid="vo-preview-btn"
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs font-semibold text-ink-secondary hover:text-ink-primary transition">
                  {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  {playing ? "Stop" : "Preview"}
                </button>
                <button type="button" data-testid="vo-rerecord-btn"
                  onClick={() => { stopPlayback(); setPhase("idle"); setSecs(0); }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs text-ink-muted hover:text-ink-primary transition">
                  <RotateCcw className="h-3.5 w-3.5" /> Re-record
                </button>
              </div>
              <button type="button" onClick={useRecording} disabled={busy} data-testid="vo-use-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-amber px-5 py-2 text-xs font-semibold text-black disabled:opacity-50 transition">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Use this recording
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
